import { SelectionChangedEvent } from "ag-grid-community";
import { StdProposalCopyToEntity } from "domain/entity/StandardProposal/StdProposalCopyToEntity";
import { StdProposalCopyToNewProposalEntity } from "domain/entity/StandardProposal/StdProposalCopyToNewProposalEntity";
import { StdProposalCopyToProposalEntity } from "domain/entity/StandardProposal/StdProposalCopyToProposalEntity";
import { ProposalCopyingForRecalConstant } from "presentation/constant/ProposalCopyingForRecal/ProposalCopyingForRecalConstant";
import { INITIAL_STD_PROPOSAL_COPY_TO_COL_DEF } from "presentation/constant/StandardProposal/StdProposalCopyToColumnDefinition";
import { INITIAL_STD_PROPOSAL_COPY_TO_NEW_PROPOSAL_COL_DEF } from "presentation/constant/StandardProposal/StdProposalCopyToNewProposalColumnDefinition";
import { INITIAL_STD_PROPOSAL_COPY_TO_PROPOSAL_COL_DEF } from "presentation/constant/StandardProposal/StdProposalCopyToProposalColumnDefinition";
import { useProposalCopyingForRecalVM } from "presentation/hook/ProposalCopyingForRecal/useProposalCopyingForRecalVM";
import { useProposalCopyingForRecalTracked } from "presentation/store/ProposalCopyingForRecal/ProposalCopyingForRecalProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo } from "react";
import { FieldType, IconButton, IFieldValue } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CopyStdProposalForRecalPanel: React.FC = () => {

    const SCREEN_CONSTANT = ProposalCopyingForRecalConstant.Title;
    const [proposalCopyingForRecalState] = useProposalCopyingForRecalTracked();
    const proposalCopyingForRecalVM = useProposalCopyingForRecalVM();
    const { companyCodeList, isCopyToNewProposal, copyToPercentageList, copyToProposalList, copyToNewProposalList, isEditNewProposalNo, isEditPercentage, isEditProposalNo, isShowPreviewPanel } = proposalCopyingForRecalState;

    const handClose = useCallback(() => {
        proposalCopyingForRecalVM.onCopyToCompanyCloseClick();
    }, [proposalCopyingForRecalVM]);

    const memoCompanyCodeList = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "650px", marginBottom: "20px", marginLeft: "10px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={''}
                fieldValue={companyCodeList ?? []}
                fieldLabel={SCREEN_CONSTANT.COMPANY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyCodeList'}
                maxLength={60}
                isMultipleDropDown={true}
                isLimitOptions={true}
                options={proposalCopyingForRecalState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    proposalCopyingForRecalVM.onCopyToFieldChange(isCopyToNewProposal, fieldKey, fieldValue, fFullValue)} />
        </div>
        , [companyCodeList, SCREEN_CONSTANT.COMPANY_CODE, proposalCopyingForRecalState.dynamicOptions.companyCodeDropdownOptions, proposalCopyingForRecalVM, isCopyToNewProposal])


    const handlePercentageSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        proposalCopyingForRecalVM.updateSelectedPercentageRow(selectedRows);
    }, [proposalCopyingForRecalVM])

    const handleProposalNoSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        proposalCopyingForRecalVM.updateSelectedProposalNoRow(selectedRows);
    }, [proposalCopyingForRecalVM])

    const handlePercentageDoubleClick = useCallback((entity: StdProposalCopyToEntity) => {
        proposalCopyingForRecalVM.onPercentageDoubleClick(entity);
    }, [proposalCopyingForRecalVM])

    const handleProposalNoDoubleClick = useCallback((entity: StdProposalCopyToProposalEntity) => {
        proposalCopyingForRecalVM.onProposalNoDoubleClick(entity);
    }, [proposalCopyingForRecalVM])

    const handleNewProposalNoDoubleClick = useCallback((entity: StdProposalCopyToNewProposalEntity) => {
        proposalCopyingForRecalVM.onNewProposalNoDoubleClick(entity);
    }, [proposalCopyingForRecalVM])

    const handleNewProposalNoSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        proposalCopyingForRecalVM.updateSelectedNewProposalNoRow(selectedRows);
    }, [proposalCopyingForRecalVM])

    const memoStdProposalCopyToTable = useMemo(() => {
        return (
            <NbisTable
                headerLabel={"Tariff List"}
                id='standard-proposal-table'
                isNewColumnSetting={true}
                columns={INITIAL_STD_PROPOSAL_COPY_TO_COL_DEF}
                data={(copyToPercentageList?.slice() ?? [])}
                showPaginator={false}
                editable={false}
                showUploadIcon={false}
                showAddIcon={false}
                showDeleteButton={false}
                showReloadIcon={false}
                isScrollHighlighted={true}
                selectionMode={false}
                rowSelection={"single"}
                isRowHighligted={true}
                onRowDoubleClick={(e: any, entity: StdProposalCopyToEntity) => handlePercentageDoubleClick(entity)}
                handleSelectionChanged={handlePercentageSelectionChange}
                gridHeight="customHeight"
                customHeight="calc(100vh - 630px)"
                showPivotMode={false}
            />
        );
    }, [copyToPercentageList, handlePercentageDoubleClick, handlePercentageSelectionChange])

    const memoCopyToProposalTable = useMemo(() => {

        return (
            <NbisTable
                headerLabel={"Proposal List"}
                id='standard-proposal-table'
                isNewColumnSetting={true}
                columns={INITIAL_STD_PROPOSAL_COPY_TO_PROPOSAL_COL_DEF}
                data={(copyToProposalList?.slice() ?? [])}
                showPaginator={false}
                editable={false}
                showUploadIcon={false}
                showAddIcon={false}
                showDeleteButton={false}
                showReloadIcon={false}
                isScrollHighlighted={true}
                selectionMode={false}
                rowSelection={"single"}
                isRowHighligted={true}
                onRowDoubleClick={(e: any, entity: StdProposalCopyToProposalEntity) => handleProposalNoDoubleClick(entity)}
                handleSelectionChanged={handleProposalNoSelectionChange}
                gridHeight="customHeight"
                customHeight="calc(100vh - 650px)"
                showPivotMode={false}
            />
        );
    }, [copyToProposalList, handleProposalNoDoubleClick, handleProposalNoSelectionChange])

    const memoCopyToNewProposalTable = useMemo(() => {

        return (
            <NbisTable
                headerLabel={"Proposal List"}
                id='standard-proposal-table'
                isNewColumnSetting={true}
                columns={INITIAL_STD_PROPOSAL_COPY_TO_NEW_PROPOSAL_COL_DEF}
                data={(copyToNewProposalList?.slice() ?? [])}
                showPaginator={false}
                editable={false}
                showUploadIcon={false}
                showAddIcon={false}
                showDeleteButton={false}
                showReloadIcon={false}
                isScrollHighlighted={true}
                selectionMode={false}
                rowSelection={"single"}
                isRowHighligted={true}
                onRowDoubleClick={(e: any, entity: StdProposalCopyToNewProposalEntity) => handleNewProposalNoDoubleClick(entity)}
                handleSelectionChanged={handleNewProposalNoSelectionChange}
                gridHeight="customHeight"
                customHeight="calc(100vh - 650px)"
                showPivotMode={false}
            />
        );
    }, [copyToNewProposalList, handleNewProposalNoDoubleClick, handleNewProposalNoSelectionChange])


    return <>
        <div className={'flex-row'} style={{ height: "30px", maxHeight: "30px" }}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <SidebarTitle style={{ marginLeft: "10px" }}>{isCopyToNewProposal ? "Copy To" : "Combine To"}</SidebarTitle>
                    <SidebarActionCross>
                        <IconButton
                            fileName="Icon-cross" size="medium" toolTipText='Close' disabled={(isEditNewProposalNo || isEditPercentage || isEditProposalNo || isShowPreviewPanel)} toolTipArrow={false} onClick={handClose} />
                    </SidebarActionCross>
                </Sidebarheader>
            </div>
        </div>

        <CriteriaItemContainer>
            {memoCompanyCodeList}
        </CriteriaItemContainer>
        <TableWrapper> {memoStdProposalCopyToTable} </TableWrapper>
        <TableWrapper> {isCopyToNewProposal ? memoCopyToNewProposalTable : memoCopyToProposalTable} </TableWrapper>
    </>;
}

export default memo(CopyStdProposalForRecalPanel);
